import { Box, Card, Flex, Table, Text, Title } from "@mantine/core";
import { ArrowRightIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import type { IAmendmentPending } from "../../apis/invoice";
import { currencyFormat } from "../../utils";
import { lineTotal } from "../../utils/invoice-totals";
import grid from "../ui/data-grid.module.css";

type LineRow = {
  key: string;
  name: string;
  quantity: number;
  price: number;
  total: number;
  unit?: string;
};

type AmendmentDiffProps = {
  currentLines: any[];
  currentAdditionalLines?: any[];
  pending: IAmendmentPending;
};

const unitName = (u: any) => (typeof u === "string" ? u : (u?.name ?? "-"));

// Both sides use lineTotal (price x qty less the line's own discount) so a
// discounted line compares like for like.
function toRow(li: any, key: string, name: string): LineRow {
  return {
    key,
    name,
    quantity: Number(li?.quantity ?? 0),
    price: Number(li?.price ?? 0),
    total: lineTotal(li),
    unit: unitName(li?.unit),
  };
}

function currentRows(lines: any[], additional: any[], manualLabel: string): LineRow[] {
  return [
    ...lines.map((li, i) => toRow(li, `l-${li.id ?? i}`, li.name ?? li.nameMon ?? li.skuCode ?? "-")),
    ...additional.map((li, i) => toRow(li, `a-${li.id ?? i}`, `${li.name ?? "-"} (${manualLabel})`)),
  ];
}

function proposedRows(pending: IAmendmentPending, current: any[], manualLabel: string): LineRow[] {
  const nameByVariant = new Map<string, any>();
  current.forEach((li) => li?.variantId && nameByVariant.set(li.variantId, li));

  return [
    ...(pending.lines?.lines ?? []).map((li, i) => {
      const known = nameByVariant.get(li.variantId);
      const name = li.name || known?.name || known?.nameMon || (li.skuCode ? `SKU ${li.skuCode}` : `SKU ${li.variantId}`);
      return { ...toRow(li, `l-${li.variantId}-${i}`, name), unit: unitName(known?.unit) };
    }),
    ...(pending.lines?.additionalLines ?? []).map((li, i) => toRow(li, `a-${i}`, `${li.name} (${manualLabel})`)),
  ];
}

function DiffTable({ title, rows }: { title: string; rows: LineRow[] }) {
  const { t } = useTranslation(["invoice", "common"]);

  return (
    <Box>
      <Text size="sm" fw={600} mb="xs">
        {title}
      </Text>
      {rows.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t("common:noData")}
        </Text>
      ) : (
        <Table className={grid.table} data-density="compact" horizontalSpacing={0} verticalSpacing={0} withRowBorders={false}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("detail.lineCol.name")}</Table.Th>
              <Table.Th ta="right">{t("detail.lineCol.qty")}</Table.Th>
              <Table.Th>{t("detail.lineCol.unit")}</Table.Th>
              <Table.Th ta="right">{t("detail.lineCol.unitPrice")}</Table.Th>
              <Table.Th ta="right">{t("detail.lineCol.total")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.key}>
                <Table.Td>{row.name}</Table.Td>
                <Table.Td ta="right">{row.quantity}</Table.Td>
                <Table.Td>{row.unit}</Table.Td>
                <Table.Td ta="right">{currencyFormat(row.price).replace(" ₮", "")}</Table.Td>
                <Table.Td ta="right">{currencyFormat(row.total).replace(" ₮", "")}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Box>
  );
}

export function AmendmentDiff({ currentLines, currentAdditionalLines = [], pending }: AmendmentDiffProps) {
  const { t } = useTranslation(["invoice", "common"]);
  const manualLabel = t("amend.additionalLine");

  const before = currentRows(currentLines, currentAdditionalLines, manualLabel);
  const after = proposedRows(pending, currentLines, manualLabel);
  const discountValue = Number(pending.lines?.discountValue ?? 0);
  const shipping = Number(pending.lines?.shippingAmount ?? 0);

  return (
    <Card withBorder radius="md" p="md">
      <Title order={4} mb="md">
        {t("amend.diffTitle")}
      </Title>
      <Flex direction={{ base: "column", md: "row" }} gap="lg" align="stretch">
        <Box flex={1}>
          <DiffTable title={t("amend.currentLines")} rows={before} />
        </Box>
        <Flex align="center" justify="center" visibleFrom="md">
          <ArrowRightIcon size={20} />
        </Flex>
        <Box flex={1}>
          <DiffTable title={t("amend.proposedLines")} rows={after} />
          {(discountValue > 0 || shipping > 0) && (
            <Flex gap="md" mt="xs" wrap="wrap">
              {discountValue > 0 && (
                <Text size="xs" c="dimmed">
                  {t("amend.discount")}: {pending.lines?.discountType === "PERCENTAGE" ? `${discountValue}%` : currencyFormat(discountValue)}
                </Text>
              )}
              {shipping > 0 && (
                <Text size="xs" c="dimmed">
                  {t("amend.shipping")}: {currencyFormat(shipping)}
                </Text>
              )}
            </Flex>
          )}
        </Box>
      </Flex>
      <Flex gap="md" mt="lg" wrap="wrap">
        <Text size="sm" fw={500}>
          {t("amend.previousTotal")}: {currencyFormat(pending.previousTotal)}
        </Text>
        <ArrowRightIcon size={14} />
        <Text size="sm" fw={600} c="gray.9">
          {t("amend.newTotal")}: {currencyFormat(pending.newTotal)}
        </Text>
      </Flex>
    </Card>
  );
}
